import Tooltip from '@material-ui/core/Tooltip';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import CountryFlag from '@components/CountryFlag';

import ConfirmationModal from '@features/crm/components/ConfirmationModal';
import { renderProfilePic, setDefaultValueForTree, toggleTreeList } from '@features/crm/utils/helpers';
import CountryWiseBreakDown from '@features/crm/v3/target/components/CountryWiseBreakDown';
import SetTarget from '@features/crm/v3/target/components/SetTarget';

import { closeLoader, openLoader } from '@store/actions/LoaderAction';

import { deleteCRMTarget, getCRMTargetListByUser } from '@services/crmService';

import { getNumberUnit, getShortName, getUserInfo, isCRMManagement } from '@utils/Util';

const TargetList = ({ isIndividualUser }) => {
	const filterData = useSelector((store) => store.filterFieldStore);
	const [openConfirmation, setOpenConfirmation] = useState(false);
	const [isAssignedClick, setIsAssignedClick] = useState(false);
	const [confirmationInfo, setConfirmationInfo] = useState({});
	const [selectedTarget, setSelectedTarget] = useState(null);
	const [isEditClick, setIsEditClick] = useState(false);
	const [data, setData] = useState([]);
	const dispatch = useDispatch();

	const generateParams = () => {
		let params = ``;
		if (filterData?.year) {
			params += `?year=${filterData['year'][0]?.value}`;
		}
		if (filterData?.countryId) {
			params += `&countryId=${filterData['countryId'][0]?.key}`;
		}
		return params;
	};

	const fetchAllTargets = async () => {
		try {
			if (filterData?.year) {
				dispatch(openLoader());
				const params = generateParams();
				let apiData = await getCRMTargetListByUser(params);
				let modifiedData = setDefaultValueForTree(apiData, 'childTargetResponseList');
				setData(modifiedData);
				dispatch(closeLoader());
			}
		} catch {
			dispatch(closeLoader());
		}
	};

	useEffect(() => {
		(async () => {
			await fetchAllTargets();
		})();
	}, [filterData]);

	const returnChildTarget = (item) => {
		return item?.childTargetResponseList?.map((target, index) => {
			return renderCard(target, index, false);
		});
	};

	const toggleTreeNode = (isRootNode, nodeInfo) => {
		const modifiedData = toggleTreeList(data, isRootNode, nodeInfo, 'childTargetResponseList');
		setData(modifiedData);
	};

	const onCloseConfirmationModal = () => {
		setConfirmationInfo({});
		setOpenConfirmation(false);
	};

	const calculateAssignedPercentage = (targetAmount = 0, assignedAmount = 0) => {
		if (targetAmount === 0) {
			return 0;
		}
		if (assignedAmount) {
			return (assignedAmount / targetAmount) * 100;
		}
		return 100;
	};

	const renderCard = (item, idx, isRootNode = false) => {
		const salePercentage = calculateAssignedPercentage(item.targetAmount, item.assignedAmount);
		if (item.isOpen) {
			const renderAssigneeInfo = () => {
				if (isRootNode) {
					return (
						<div className="d-flex align-items-center gap-2">
							{renderProfilePic(item.targetSetBy)}
							<img
								src={`${process.env.PUBLIC_URL}/icons/right-arrow.svg`}
								alt="people"
								className="arrow"
							/>
							{renderProfilePic(item.targetSetTo)}
							<span className="regular-14 gray_dark_01">
								{item?.targetSetTo?.name}
								{item?.targetSetTo?.id === getUserInfo().id ? '(You)' : ''}
							</span>
							{isCRMManagement() && (
								<span className="flag">
									<CountryFlag countryId={item.country.id} />
								</span>
							)}
							{item?.quarter && (
								<div>
									<span className="status purple-1">
										{item?.quarter}-{item.year}
									</span>
								</div>
							)}
						</div>
					);
				} else {
					return (
						<div className="d-flex align-items-center gap-2">
							{renderProfilePic(item.targetSetTo)}
							<span className="regular-14 gray_dark_01">{item?.targetSetTo?.name}</span>
						</div>
					);
				}
			};

			return (
				<li className="opened view" key={`tree_${idx}`}>
					<div
						className={`single-target-component ${!item?.id ? 'empty-target' : ''} ${
							isRootNode ? 'parent' : ''
						}`}
					>
						{item?.childTargetResponseList?.length > 0 && (
							<div className="expand-action">
								<button
									className="button text-only"
									onClick={() => toggleTreeNode(isRootNode, item)}
								>
									{item.isChildOpen && (
										<img
											src={`${process.env.PUBLIC_URL}/icons/row-close.svg`}
											alt="people"
										/>
									)}
									{!item.isChildOpen && (
										<img
											src={`${process.env.PUBLIC_URL}/icons/row-open.svg`}
											alt="people"
										/>
									)}
								</button>
							</div>
						)}
						<div className="assignee-and-status">{renderAssigneeInfo()}</div>
						{item?.id && (
							<>
								<div className="brand-info d-flex justify-content-center align-items-center">
									<div className="logo-info">
										{item?.brand?.brandLogoDocResponse?.docUrl && (
											<img
												src={item.brand.brandLogoDocResponse?.docUrl}
												alt="people"
												className="people"
											/>
										)}
									</div>
									<div className="name-info">
										{item?.brand?.name && (
											<Tooltip
												title={
													<span className="regular-14">{item?.brand?.name}</span>
												}
												placement="top"
												arrow
											>
												<p className="semibold-14">
													{getShortName(item?.brand?.name, 11)}
												</p>
											</Tooltip>
										)}
										{!item?.brand?.name && <p className="semibold-14">N/A</p>}

										<p className="regular-14 gray_dark_02">
											{item?.productGroup?.name || 'N/A'}
										</p>
									</div>
								</div>
								<div className="nitex-design-info">
									<p className="regular-14 gray_dark_01">Nitex Design</p>
									<div className="d-flex align-items-center gap-2">
										<p className="regular-12 gray_dark_01">
											Target:{' '}
											<span className="semibold-14">
												{item?.targetNoOfNitexDesign || 0}
											</span>
										</p>
										<p className="regular-12 weight-500 gray_dark_02">|</p>
										<p className="regular-12 gray_dark_01">
											Assigned:{' '}
											<span className="semibold-14">
												{item?.assignedNoOfNitexDesign || 0}
											</span>
										</p>
									</div>
								</div>
								<div className="sales-info">
									<p className="regular-14 mb-2">Sales</p>
									{item.assignedAmount ? (
										<Tooltip
											title={
												<>
													<p className="regular-14">
														Assigned: {getNumberUnit(item.assignedAmount)}
													</p>
													<p className="regular-14">
														Unassigned:{' '}
														{getNumberUnit(
															item.targetAmount - item.assignedAmount
														)}
													</p>
												</>
											}
											placement="top"
											arrow
										>
											<div className="progress progress-square">
												<div
													className="progress-bar"
													role="progressbar"
													style={{
														width: `${salePercentage}%`
													}}
													aria-valuenow={salePercentage}
													aria-valuemin={0}
													aria-valuemax={100}
												/>
											</div>
										</Tooltip>
									) : (
										<div className="progress progress-square">
											<div
												className="progress-bar"
												role="progressbar"
												style={{
													width: `${salePercentage}%`
												}}
												aria-valuenow={salePercentage}
												aria-valuemin={0}
												aria-valuemax={100}
											/>
										</div>
									)}
									<p className="d-flex justify-content-between">
										<span className="gray_dark_01 regular-12">Assigned</span>
										<span className="semibold-12 gray_dark_02">
											{getNumberUnit(item.targetAmount)}
										</span>
									</p>
								</div>
							</>
						)}
						{renderActions(item, isRootNode)}
					</div>
					<ul>{returnChildTarget(item)}</ul>
				</li>
			);
		}
	};

	const renderActions = (item, isRootNode) => {
		if (isCRMManagement()) {
			return (
				<div className="actions text-right">
					<div className="dropdown">
						<button
							className="btn btn-default"
							type="button"
							id="menu1"
							data-toggle="dropdown"
							aria-expanded="true"
							disabled={!item.isEditable}
						>
							<img src={`${process.env.PUBLIC_URL}/icons/more_vertical.svg`} alt="option" />
						</button>
						<ul
							className="dropdown-menu dropdown-menu-right shadow-lg"
							role="menu"
							aria-labelledby="menu1"
						>
							<li
								data-toggle="modal"
								data-target="#add-target-modal"
								onClick={() => {
									setIsEditClick(true);
									setSelectedTarget(item);
								}}
							>
								<span>Edit</span>
							</li>
							<li
								onClick={() => {
									setConfirmationInfo({
										id: item?.id,
										title: 'Are you sure?',
										description: 'you want to delete this target?',
										footerType: 'CLOSE'
									});
									setOpenConfirmation(true);
								}}
							>
								<span className="red">Delete</span>
							</li>
						</ul>
					</div>
				</div>
			);
		} else if ((isRootNode && getUserInfo().id === item.targetSetTo.id) || !item?.id) {
			return (
				<div className="actions text-right">
					{!isIndividualUser && (
						<button
							disabled={item.targetAmount <= item.assignedAmount}
							className="button outline size36"
							data-toggle="modal"
							data-target="#add-target-modal"
							onClick={() => {
								setIsAssignedClick(true);
								setSelectedTarget(item);
							}}
						>
							Assign
						</button>
					)}
				</div>
			);
		} else {
			return (
				<div className="actions text-right">
					<button
						className="btn"
						data-toggle="modal"
						data-target="#add-target-modal"
						onClick={() => {
							setIsEditClick(true);
							setSelectedTarget(item);
						}}
						disabled={!item.isEditable}
					>
						Edit
					</button>
				</div>
			);
		}
	};

	const renderTargets = () => {
		return data?.map((item, index) => {
			return <React.Fragment key={`tree_${index}`}>{renderCard(item, index, true)}</React.Fragment>;
		});
	};

	const removeTarget = async () => {
		try {
			dispatch(openLoader());
			await deleteCRMTarget(confirmationInfo.id);
			onCloseConfirmationModal();
			await fetchAllTargets();
			dispatch(closeLoader());
		} catch {
			dispatch(closeLoader());
		}
	};
	return (
		<>
			<CountryWiseBreakDown />
			<div className="target-list">
				<div className="crm-data-tree">
					<div className="tree">
						<ul className="parent-layer">{renderTargets()}</ul>
					</div>
				</div>
			</div>

			<SetTarget
				fetchAllTargets={fetchAllTargets}
				selectedTarget={selectedTarget}
				isAssignedClick={isAssignedClick}
				isEditClick={isEditClick}
				clearSelection={() => {
					setIsAssignedClick(false);
					setSelectedTarget(null);
					setIsEditClick(false);
				}}
			/>
			{openConfirmation && (
				<ConfirmationModal
					title={confirmationInfo?.title}
					closeModal={onCloseConfirmationModal}
					description={confirmationInfo?.description}
					callback={removeTarget}
					footerType={confirmationInfo?.footerType}
				/>
			)}
		</>
	);
};

export default TargetList;
